/* RequestScanVisitModal — iter97.97 (unified + redesigned)
 *
 * Schedules a technician intraoral-scan visit for a patient AND mints a
 * stub case (status `scan_visit_scheduled`) in the same round-trip.
 *
 * Previously had two operating modes — "standalone" (from Patient
 * Profile) without payment options, and "wizard_quick_create" (from
 * the Case Wizard) WITH payment options. The two flows looked
 * different and behaved differently, which was confusing. This
 * version unifies them: every scan-visit request now creates a case
 * and captures the payment choice up front, regardless of entry
 * point. Visually rewritten for clarity — primary-teal palette,
 * single payment tile, generous spacing.
 *
 * Props:
 *   open: boolean
 *   onClose(): void
 *   onCreated(visit): void   — fired after a successful create
 *   patient: { id, name }
 *   fee:     number | null   — admin-set per-scan fee. When null,
 *                              the modal fetches it from
 *                              /api/settings/io-scanner-fee on open.
 *   mode:    string | undefined — kept for backward compat; ignored.
 */
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    CalendarPlus,
    Check,
    Receipt,
    User,
    Info,
    Clock,
    Camera,
    CloudArrowUp,
    Crown,
    ShieldCheck,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

// iter98.99dt — Expanded slot range from a tight 09:00–16:00 grid to a
// full 09:00–22:00 grid (one slot per hour, last slot 21:00 → 22:00 end)
// so doctors at clinics that operate late evenings can book scans for
// patients who walk in after 5 PM. The lunch gap (12–14) was filled too —
// many Indian clinics don't take a break, and self-plan doctors mid-day
// were asking for noon slots. Past-slot filtering still hides anything
// that's already started, so this list is the SUPERSET of bookable times.
const TIME_SLOTS = [
    "09:00", "10:00", "11:00", "12:00", "13:00",
    "14:00", "15:00", "16:00", "17:00", "18:00",
    "19:00", "20:00", "21:00",
];

// iter98.67 — Helpers to detect whether a slot is already in the past
// for the clinic timezone (Asia/Kolkata is the single supported region
// today; mirrors backend `_validate_not_in_past`). The doctor's browser
// may be in a different tz, so we normalize to IST before comparing.
function _istParts() {
    // toLocaleString with the right tz options gives us the wall-clock
    // values for India regardless of the user's local tz.
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Kolkata",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = fmt.formatToParts(new Date()).reduce((acc, p) => {
        acc[p.type] = p.value;
        return acc;
    }, {});
    return {
        date: `${parts.year}-${parts.month}-${parts.day}`,
        time: `${parts.hour}:${parts.minute}`,
    };
}

function isPastSlot(dateStr, timeStr) {
    const { date, time } = _istParts();
    if (dateStr < date) return true;
    if (dateStr > date) return false;
    // Same day in IST — compare slot vs current wall-clock.
    return timeStr <= time;
}

// iter98.99k — Compute the next valid IST date (YYYY-MM-DD) that has at
// least one bookable slot. Used to auto-bump the picker when today is
// fully past. Walks forward up to 14 days as a safety bound.
function nextBookableDate(fromDateStr) {
    let cursor = fromDateStr;
    for (let i = 0; i < 14; i += 1) {
        if (TIME_SLOTS.some((t) => !isPastSlot(cursor, t))) return cursor;
        // Increment cursor by one day (string-safe).
        const d = new Date(`${cursor}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + 1);
        cursor = d.toISOString().slice(0, 10);
    }
    return fromDateStr;
}

// iter98.60 — one-time soft toast key. Flipped to "1" on the first
// successful scan-visit booking on this browser so we explain the new
// "always added to final invoice" billing once and never again.
const BILLING_INFO_TOAST_KEY = "invsq_scan_billing_info_seen";

function fmtSlot(t) {
    const h = Number(t.split(":")[0]);
    const ampm = (n) => (n >= 12 ? "PM" : "AM");
    const hr = (n) => {
        const x = n % 12;
        return String(x === 0 ? 12 : x).padStart(2, "0");
    };
    const next = h + 1;
    return `${hr(h)}:00 ${ampm(h)} – ${hr(next)}:00 ${ampm(next)}`;
}

export default function RequestScanVisitModal({ open, onClose, onCreated, patient, fee = null, planningMode = null }) {
    // iter98.99k — Initial picker date now intelligently skips fully-
    // past days so the doctor opens the modal already on a bookable
    // day. Was a static "today" which on a late-evening open showed an
    // empty / disabled slot picker.
    const today = new Date().toISOString().slice(0, 10);
    const initialDate = nextBookableDate(today);
    const initialTime = (TIME_SLOTS.find((t) => !isPastSlot(initialDate, t))) || TIME_SLOTS[0];
    const [date, setDate] = useState(initialDate);
    const [time, setTime] = useState(initialTime);
    const [notes, setNotes] = useState("");
    // iter98.59 — Lab-visit fees now always roll into the final case
    // invoice. The "Pay separately / upfront" option has been removed
    // from the UI entirely. The constant below is kept (rather than
    // inlined) so the request payload below reads identically to the
    // earlier signature and stays trivially auditable.
    const paymentChoice = "final_invoice";
    const [submitting, setSubmitting] = useState(false);

    // Live fee — defaults to the prop if provided, otherwise fetched
    // from the same admin-managed endpoint the wizard uses. The patient
    // profile entry-point doesn't pass a fee prop, so the modal lazy-
    // loads it the first time it opens.
    const [liveFee, setLiveFee] = useState(fee);
    useEffect(() => {
        if (!open) return;
        if (fee != null) {
            setLiveFee(fee);
            return;
        }
        api.get("/settings/io-scanner-fee")
            .then((r) => setLiveFee(Number(r.data?.fee ?? 3000)))
            .catch(() => setLiveFee(3000));
    }, [open, fee]);

    // Reset form whenever the modal closes so re-opens start clean —
    // recompute the bookable day each open so a modal that was first
    // mounted in the morning still skips today if the user re-opens it
    // in the evening when all slots have passed.
    useEffect(() => {
        if (!open) {
            const freshDate = nextBookableDate(new Date().toISOString().slice(0, 10));
            const freshTime = (TIME_SLOTS.find((t) => !isPastSlot(freshDate, t))) || TIME_SLOTS[0];
            setDate(freshDate);
            setTime(freshTime);
            setNotes("");
            setSubmitting(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // iter98.67 → iter98.99k — When the doctor switches date, recompute
    // which slots are bookable and auto-pick the next available one. The
    // dropdown itself now hides past slots entirely (no more "(passed)"
    // labels) so the user never sees a disabled option.
    useEffect(() => {
        if (!date) return;
        if (isPastSlot(date, time)) {
            const nextOk = TIME_SLOTS.find((t) => !isPastSlot(date, t));
            if (nextOk) setTime(nextOk);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // iter98.99k — Slots that haven't passed yet for the chosen date.
    // Used to render the time-select with future-only options. Memoised
    // off (date) so the list updates instantly when the doctor picks a
    // different day.
    const availableSlots = TIME_SLOTS.filter((t) => !isPastSlot(date, t));
    const hasBookableSlot = availableSlots.length > 0;

    const onSubmit = async () => {
        if (!patient?.id) return;
        if (!date || !time) {
            toast.error("Please pick a date and time slot");
            return;
        }
        // iter98.67 — Client-side guard mirroring the backend
        // `_validate_not_in_past`. Catches the case where the user
        // bypassed the disabled <option> via dev-tools or stale state.
        if (isPastSlot(date, time)) {
            toast.error("Please pick a future time — that slot is already in the past.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                date,
                time,
                case_id: null,
                technician_name: null,
                notes: notes.trim() || "",
                auto_create_case: true,
                payment_choice: paymentChoice,
                fee_at_booking: liveFee != null ? Number(liveFee) : null,
                // iter98.56 — Pass the wizard's already-selected planning
                // mode through so the stub case remembers it. The resume
                // flow then renders the planning-mode card as locked
                // instead of asking the doctor to pick again.
                planning_mode: planningMode || null,
            };
            const { data } = await api.post(
                `/doctor/patients/${patient.id}/scan-visits`,
                payload,
            );
            // iter98.99ds — Fire conversion event for the Book Scan Visit
            // funnel. Pairs with the click event on the rose CTA so the
            // admin scan-visit-funnel report can compute conversion %.
            trackEvent("scan_visit_booking_confirmed", {
                patient_id: patient?.id,
                case_id: data?.case_id || null,
                visit_id: data?.id || data?.visit_id || null,
                date,
                time,
                fee: liveFee != null ? Number(liveFee) : null,
                planning_mode: planningMode || null,
            });
            /* iter98.99g — Replaced the thin sonner toast + delayed
               billing-info toast with a celebratory confirmation dialog
               rendered by the parent (CaseSubmissionPage / PatientProfile).
               We forward the booking metadata so the dialog body can show
               the date + time. The localStorage one-time billing-info
               nudge is now folded into the dialog body itself, so the
               cross-toast collision is gone too. */
            onCreated?.({
                ...data,
                __booking_meta: {
                    date,
                    time,
                    time_label: fmtSlot(time),
                    patient_name: patient?.name || patient?.full_name || "Patient",
                    fee: liveFee != null ? Number(liveFee) : null,
                },
            });
            onClose?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Failed to book scan visit";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const feeLabel =
        liveFee != null
            ? `₹${Number(liveFee).toLocaleString("en-IN")}`
            : "Live pricing";

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                /* iter98.99j — Compact-mode redesign so the modal fits on
                   laptop viewports (≤768 px tall) without overflowing.
                   max-h:90vh + internal scroll, max-w:md (down from lg),
                   and per-section paddings/spacing trimmed across the
                   board. The previous layout was clipping on 13" laptops
                   below 800 px tall. */
                className="max-w-md p-0 gap-0 overflow-hidden flex flex-col max-h-[88vh]"
                data-testid="request-scan-visit-modal"
            >
                {/* HEADER — clean bar with primary accent. */}
                <div className="px-5 pt-4 pb-3 bg-gradient-to-r from-primary/5 via-primary/[0.03] to-transparent border-b border-border shrink-0">
                    <div className="flex items-start gap-2.5 pr-7">
                        <span className="w-8 h-8 rounded-lg bg-primary text-white grid place-items-center shrink-0">
                            <CalendarPlus size={15} weight="bold" />
                        </span>
                        <div>
                            <div className="font-display text-[15px] font-bold leading-tight">
                                Book Invisique™ scan visit
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-snug">
                                Trained technician visits your clinic, scans &amp; uploads to the case.
                            </div>
                        </div>
                    </div>
                </div>

                {/* BODY — scrolls internally if the viewport is too short. */}
                <div className="px-5 py-4 space-y-3.5 overflow-y-auto flex-1 min-h-0">
                    {/* iter98.99dr — Compact value-prop hero. Mirrors the
                        full-fat hero rendered by Step 1 of the Case Wizard
                        (CaseSubmissionPage.jsx) so doctors who land here
                        from the Patient Header (rather than the wizard)
                        get the same "why this service is worth it" story
                        before they pick a slot. Kept tighter than the
                        wizard variant — three icon tiles + a sharp
                        headline — to fit the modal width without
                        overwhelming the form below. */}
                    <div
                        className="relative overflow-hidden rounded-xl border border-primary/25 bg-gradient-to-br from-primary/[0.07] via-white to-primary/[0.13] px-4 pt-3.5 pb-3.5"
                        data-testid="req-scan-hero"
                    >
                        <div aria-hidden="true" className="pointer-events-none absolute -top-12 -right-12 w-32 h-32 rounded-full bg-primary/15 blur-3xl" />
                        <div className="relative">
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-[0.16em] shadow-sm bg-primary text-primary-foreground"
                                    data-testid="req-scan-hero-eyebrow"
                                >
                                    <Crown size={10} weight="fill" /> White-glove records
                                </span>
                                <span className="inline-flex items-center gap-1 text-[9.5px] font-semibold tracking-wider text-primary uppercase">
                                    <ShieldCheck size={10} weight="duotone" /> Free re-scan guarantee
                                </span>
                            </div>
                            <h3
                                className="font-display text-[15px] font-bold text-foreground leading-tight tracking-tight"
                                data-testid="req-scan-hero-headline"
                            >
                                Get 45 minutes of chair-time back. Every case.
                            </h3>
                            <p
                                className="text-[11px] text-foreground/70 mt-1 leading-snug"
                                data-testid="req-scan-hero-subtitle"
                            >
                                A trained tech captures a 3D IO scan + DSLR clinical series while you keep treating revenue patients.
                            </p>
                            <div className="mt-3 grid grid-cols-3 gap-1.5" data-testid="req-scan-hero-tiles">
                                {[
                                    { Icon: Clock,        label: "Chair time",   value: "45 min" },
                                    { Icon: Camera,       label: "DSLR series",  value: "4-angle" },
                                    { Icon: CloudArrowUp, label: "Upload",       value: "Same-day" },
                                ].map((tile, i) => {
                                    const TIcon = tile.Icon;
                                    return (
                                        <div
                                            key={i}
                                            className="rounded-lg bg-white/85 backdrop-blur-sm border border-primary/20 px-2 py-2 text-center"
                                            data-testid={`req-scan-hero-tile-${i}`}
                                        >
                                            <div className="w-6 h-6 rounded-md bg-primary/10 text-primary grid place-items-center mx-auto">
                                                <TIcon size={12} weight="duotone" />
                                            </div>
                                            <div className="mt-1 text-[8.5px] font-bold uppercase tracking-[0.1em] text-muted-foreground leading-none">
                                                {tile.label}
                                            </div>
                                            <div className="mt-0.5 font-display text-[11.5px] font-bold text-foreground leading-tight tabular-nums">
                                                {tile.value}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    {/* Patient chip */}
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-secondary/60 border border-border">
                        <User size={12} weight="duotone" className="text-muted-foreground" />
                        <span className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
                            Patient
                        </span>
                        <span className="text-[13px] font-semibold text-foreground ml-auto" data-testid="req-scan-patient">
                            {patient?.name || "—"}
                        </span>
                    </div>

                    {/* Date + Time tile */}
                    <div>
                        <Label className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
                            When should the technician arrive?
                        </Label>
                        <div className="mt-1.5 grid grid-cols-2 gap-2">
                            <Input
                                type="date"
                                value={date}
                                min={today}
                                onChange={(e) => setDate(e.target.value)}
                                className="h-9 text-[13px]"
                                data-testid="req-scan-date"
                            />
                            {/* iter98.99k — Future-only slot dropdown. Past
                                slots are hidden entirely rather than rendered
                                as disabled "(passed)" options — matches what
                                Calendly / Cal.com / Resy do. When today is
                                fully booked-out the picker shows a friendly
                                empty pill + one-click "Jump to next day". */}
                            {hasBookableSlot ? (
                                <select
                                    value={time}
                                    onChange={(e) => setTime(e.target.value)}
                                    className="w-full h-9 text-[13px] rounded-md border border-border bg-card px-2.5 outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                                    data-testid="req-scan-time"
                                >
                                    {availableSlots.map((t) => (
                                        <option key={t} value={t}>{fmtSlot(t)}</option>
                                    ))}
                                </select>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => {
                                        // Bump to the next day that has at
                                        // least one bookable slot, then pick
                                        // its first slot.
                                        const d = new Date(`${date}T00:00:00Z`);
                                        d.setUTCDate(d.getUTCDate() + 1);
                                        const tomorrow = d.toISOString().slice(0, 10);
                                        const nextDate = nextBookableDate(tomorrow);
                                        setDate(nextDate);
                                        const firstSlot = TIME_SLOTS.find((t) => !isPastSlot(nextDate, t));
                                        if (firstSlot) setTime(firstSlot);
                                    }}
                                    className="w-full h-9 text-[12px] rounded-md border border-dashed border-primary/40 bg-primary/5 text-primary font-semibold px-2.5 outline-none hover:bg-primary/10 transition-colors"
                                    data-testid="req-scan-jump-next-day"
                                >
                                    Fully booked today · Jump to next day
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notes — compact two-row textarea */}
                    <div>
                        <Label className="text-[10.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground">
                            Notes for the technician <span className="normal-case font-normal text-muted-foreground/70 tracking-normal ml-1">(optional)</span>
                        </Label>
                        <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="e.g. Lower-arch braces — handle bite registration carefully…"
                            rows={2}
                            className="mt-1.5 text-[13px] resize-none"
                            data-testid="req-scan-notes"
                        />
                    </div>

                    {/* iter98.99j — Combined fee + billing + info into ONE
                        compact tile so we save 3 sections worth of height.
                        Was: separate Per-scan fee label, "Added to final
                        invoice" card, and a free-floating Scan Visit
                        Scheduled paragraph. */}
                    <div
                        className="rounded-lg bg-primary/5 border border-primary/30 px-3 py-2.5 space-y-1.5"
                        data-testid="req-scan-billing-card"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] font-bold text-primary">
                                <Receipt size={12} weight="duotone" />
                                Per-scan fee · added to final invoice
                            </span>
                            <span className="text-[14px] font-bold tabular-nums" data-testid="req-scan-fee-amount">
                                {feeLabel}
                            </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-snug" data-testid="req-scan-pay-final-invoice">
                            Non-refundable once the visit is completed. The fee lumps with the case bill — no separate invoice.
                        </p>
                        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground leading-snug pt-1.5 border-t border-primary/15">
                            <Info size={11} weight="fill" className="text-primary shrink-0 mt-[1.5px]" />
                            <span>A new case is created in <span className="font-semibold text-foreground">Scan Visit Scheduled</span>. Once scans land, you&apos;ll resume to finish the clinical exam &amp; pricing.</span>
                        </p>
                    </div>
                </div>

                {/* FOOTER */}
                <div className="px-5 py-3 bg-secondary/30 border-t border-border flex items-center justify-end gap-2 shrink-0">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onClose}
                        className="rounded-full px-4 h-8 text-[12px]"
                        data-testid="req-scan-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={onSubmit}
                        disabled={submitting}
                        className="rounded-full px-4 h-8 text-[12px] gap-1.5"
                        data-testid="req-scan-confirm"
                    >
                        <Check size={12} weight="bold" />
                        {submitting ? "Booking…" : "Book visit & create case"}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* PaymentChoiceCard removed in iter98.59 — the modal no longer offers
 * a choice between upfront-pay and final-invoice. Scan fees are now
 * always added to the case's final invoice. */
